import React, { useState } from 'react';
import { Maximize2, X } from 'lucide-react';

interface InlineJsonDisplayProps {
  data: any;
  allowExpand?: boolean;
}

/**
 * Unescapes a JSON string to convert escape sequences to actual characters
 */
const unescapeJsonString = (str: string): string => {
  if (!str) return str;
  
  let result = '';
  let i = 0;
  
  while (i < str.length) {
    if (str[i] === '\\' && i + 1 < str.length) {
      const nextChar = str[i + 1];
      switch (nextChar) {
        case 'n':
          result += '\n';
          i += 2;
          break;
        case 'r':
          result += '\r';
          i += 2;
          break;
        case 't':
          result += '\t';
          i += 2;
          break;
        case '"':
          result += '"';
          i += 2;
          break;
        case "'":
          result += "'";
          i += 2;
          break;
        case '\\':
          result += '\\';
          i += 2;
          break;
        default:
          result += str[i];
          i++;
          break;
      }
    } else {
      result += str[i];
      i++;
    }
  }
  
  return result;
};

/**
 * Renders JSON inline without collapsible sections
 */
export const InlineJsonDisplay: React.FC<InlineJsonDisplayProps> = ({ data, allowExpand = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderValue = (value: any, key: string, depth: number = 0): React.ReactNode => {
    // Special handling for "content" fields - render as formatted text
    if (key === 'content' && typeof value === 'string') {
      const unescapedContent = unescapeJsonString(value);
      
      return (
        <div className="ml-4 my-2">
          <span className="text-blue-600 dark:text-blue-400">"content"</span>
          <span className="text-muted-foreground">: </span>
          <div className="mt-1 p-3 bg-muted/30 border border-border rounded-md">
            <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {unescapedContent}
            </div>
          </div>
        </div>
      );
    }

    if (value === null) {
      return <span className="text-orange-600 dark:text-orange-400">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="text-purple-600 dark:text-purple-400">{String(value)}</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-green-600 dark:text-green-400">{value}</span>;
    }

    if (typeof value === 'string') {
      return <span className="text-amber-700 dark:text-amber-400">"{value}"</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-muted-foreground">[]</span>;
      }
      
      return (
        <div>
          <span className="text-muted-foreground">[</span>
          <div className="ml-4">
            {value.map((item, index) => (
              <div key={index}>
                {renderValue(item, String(index), depth + 1)}
                {index < value.length - 1 && <span className="text-muted-foreground">,</span>}
              </div>
            ))}
          </div>
          <span className="text-muted-foreground">]</span>
        </div>
      );
    }

    if (typeof value === 'object') {
      // Filter out reasoning field - never display to users
      const entries = Object.entries(value).filter(([k]) => k !== 'reasoning');
      if (entries.length === 0) {
        return <span className="text-muted-foreground">{'{}'}</span>;
      }

      return (
        <div>
          <span className="text-muted-foreground">{'{'}</span>
          <div className="ml-4">
            {entries.map(([k, v], index) => (
              <div key={k} className="my-1">
                <span className="text-blue-600 dark:text-blue-400">"{k}"</span>
                <span className="text-muted-foreground">: </span>
                {renderValue(v, k, depth + 1)}
                {index < entries.length - 1 && <span className="text-muted-foreground">,</span>}
              </div>
            ))}
          </div>
          <span className="text-muted-foreground">{'}'}</span>
        </div>
      );
    }

    return String(value);
  };

  return (
    <>
      {/* Inline view with expand button */}
      <div className="relative">
        <div className="font-mono text-xs leading-relaxed">
          {renderValue(data, 'root', 0)}
        </div>
        {allowExpand && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="absolute top-2 right-2 p-1.5 bg-muted hover:bg-muted/80 rounded text-muted-foreground hover:text-foreground transition-colors"
            title="Expand to fullscreen"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Modal view */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-background border border-border rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">JSON Details</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 hover:bg-muted rounded transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-sm leading-relaxed font-mono">
                {renderValue(data, 'root', 0)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
